import { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import { Button, Form, Input, Space, Table, Tooltip, Drawer, Select, Typography, Checkbox } from 'antd';
import { useParams } from 'react-router-dom';
import { getAllExpertsApi, saveExpertsApi } from '@services/Guest';
import WorkExperiences from '@/components/common/WorkExperiences';
import ExpertInfoCard from '@/components/common/ExpertInfoCard';
import ExpertProfileDrawer from '@/components/Expert/profile';
import '../../GuestList.less';
import './index.less';
import { expertLevels } from '@utils/constants';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/utils/property';

const { Text } = Typography;

const AddGuest = props => {
    const {
        visibleAddGuestsDrawer,
        setVisibleAddGuestsDrawer,
        onResetGuest,
        setAddGuests,
        isAddAgenda = false,
        showWithinConference = false, // 是否显示会议内嘉宾筛选
    } = props;

    const params = useParams();
    const conferenceId = params?.id || 0; // 会议项目 ID

    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const [inPageSize, setInPageSize] = useState(10);
    const [inPage, setInPage] = useState(1);
    const [inTotal, setInTotal] = useState(0);

    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const isSearchRef = useRef(null);
    const [expertForm] = Form.useForm();
    const [inForm] = Form.useForm();

    const [filObj, setFilObj] = useState({
        mobile: '',
        name: '',
    });

    // 会议内嘉宾筛选
    const [withinConferenceValue, setWithinConferenceValue] = useState(false); // 默认不勾选
    const withinConferenceValueRef = useRef(false);

    // 同步 ref 的值
    useEffect(() => {
        withinConferenceValueRef.current = withinConferenceValue;
    }, [withinConferenceValue]);

    const guestSelectionTableColumns = [
        {
            title: '基本信息',
            name: 'expertLevel',
            width: 200,
            render: (_, record) => <ExpertInfoCard expert={record} />,
        },
        {
            title: '单位任职',
            name: 'units',
            width: 200,
            render: (_, record) => <WorkExperiences workExperiences={record.ssoMemberWorkExperiencesEntities} />,
        },
        {
            title: '本会议邀请方',
            name: 'organisations',
            width: 200,
            render: (_, record) => {
                const organisations = record?.organisations;
                const exclusiveId = record?.exclusiveInvitationOrgId;

                return organisations && !!organisations.length ? (
                    <span>
                        {organisations.map((item, index) => (
                            <span key={item.id || index}>
                                {item.name}
                                {exclusiveId && item.id === exclusiveId ? <span style={{ color: '#faad14' }}>(独家)</span> : null}
                                {index < organisations.length - 1 ? '、' : ''}
                            </span>
                        ))}
                    </span>
                ) : (
                    <Text type="secondary">&ndash;</Text>
                );
            },
        },
        {
            title: '选用状态',
            name: 'personalInfo',
            width: 110,
            render: (_, record) => {
                return <Fragment>{!!record.selected ? <Text type="success">本会议已选用</Text> : <Text type="danger">本会议未选用</Text>}</Fragment>;
            },
        },
    ];

    // 已选用户表格列定义（添加操作列）
    const guestSelectedTableColumns = [
        {
            title: '基本信息',
            name: 'expertLevel',
            width: 200,
            render: (_, record) => <ExpertInfoCard expert={record} />,
        },
        {
            title: '单位任职',
            name: 'units',
            width: 200,
            render: (_, record) => <WorkExperiences workExperiences={record.ssoMemberWorkExperiencesEntities} />,
        },
        {
            title: '本会议邀请方',
            name: 'organisations',
            width: 200,
            render: (_, record) => {
                const organisations = record?.organisations;

                return organisations && !!organisations.length ? organisations.map(item => item.name).join('、') : <Text type="secondary">&ndash;</Text>;
            },
        },
        {
            title: '选用状态',
            name: 'personalInfo',
            width: 110,
            render: (_, record) => {
                return <Fragment>{!!record.selected ? <Text type="success">本会议已选用</Text> : <Text type="danger">本会议未选用</Text>}</Fragment>;
            },
        },
        {
            title: '操作',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Button type="link" size="small" icon={<i className="bi bi-x-circle-fill"></i>} onClick={() => removeGuest(record)}>
                    移除
                </Button>
            ),
        },
    ];

    const [guestsToSelect, setGuestsToSelect] = useState([]);
    const [guestsSelected, setGuestsSelected] = useState([]);
    const [guestsFilter, setGuestsFilter] = useState([]);

    /** 嵌套打开专家档案抽屉（新增专家 expertId=0，或表单内跳转查看已有专家） */
    const [expertProfileDrawerOpen, setExpertProfileDrawerOpen] = useState(false);
    const [expertProfileExpertId, setExpertProfileExpertId] = useState(null);

    // 移除已选嘉宾
    const removeGuest = record => {
        // 从已选列表中移除
        const newGuestsSelected = guestsSelected.filter(item => item.id !== record.id);
        setGuestsSelected(newGuestsSelected);
        setGuestsFilter(newGuestsSelected);
        setInTotal(newGuestsSelected.length);

        // 同时更新选中状态
        const newSelected = selected.filter(id => id !== record.id);
        setSelected(newSelected);
    };

    // 添加嘉宾
    const addGuests = () => {
        const memCards = guestsSelected.map(item => item.memCard);
        const expertIds = guestsSelected.map(item => item.id);
        saveExpertsApi({ memCards: memCards, expertIds: expertIds, conferenceId }).then(res => {
            if (res.status_code === 200) {
                // message.success("添加成功");
                setVisibleAddGuestsDrawer(false);
                onResetGuest('change');
                expertForm.resetFields();

                // 兼容新增日程 - 快速添加嘉宾
                if (setAddGuests) {
                    setAddGuests(expertIds);
                }
            }
        });
    };

    const guestToSelectSelection = {
        selectedRowKeys: selected,
        onSelect: (record, select) => {
            if (select) {
                // 选中后立即添加到已选用户
                const isExist = guestsSelected.some(item => item.id === record.id);
                if (!isExist) {
                    const newGuestsSelected = [...guestsSelected, record];
                    setGuestsSelected(newGuestsSelected);
                    setGuestsFilter(newGuestsSelected);
                    setInTotal(newGuestsSelected.length);
                }

                const originalArray = [...selected, record.id];
                const uniqueArray = Array.from(new Set(originalArray));
                setSelected([...uniqueArray]);
            } else {
                // 取消选中时从已选用户中移除
                const newGuestsSelected = guestsSelected.filter(item => item.id !== record.id);
                setGuestsSelected(newGuestsSelected);
                setGuestsFilter(newGuestsSelected);
                setInTotal(newGuestsSelected.length);

                const re = selected.filter(it => it !== record.id);
                setSelected([...re]);
            }
        },
        onSelectAll: (select, selectedRows, changeRows) => {
            if (select) {
                // 全选时添加当前页所有用户到已选用户
                const newGuestsSelected = [...guestsSelected];

                // 使用 selectedRows 参数，它包含当前页所有被选中的行
                selectedRows.forEach(row => {
                    const isExist = newGuestsSelected.some(item => item.id === row.id);
                    if (!isExist) {
                        newGuestsSelected.push(row);
                    }
                });

                setGuestsSelected(newGuestsSelected);
                setGuestsFilter(newGuestsSelected);
                setInTotal(newGuestsSelected.length);

                // 更新选中的 ID 列表
                const selectedIds = selectedRows.map(i => i.id);
                const originalArray = [...selected, ...selectedIds];
                const uniqueArray = Array.from(new Set(originalArray));
                setSelected([...uniqueArray]);
            } else {
                // 全不选时从已选用户中移除当前页的所有用户
                const changeIds = changeRows.map(i => i.id);
                const newGuestsSelected = guestsSelected.filter(item => !changeIds.includes(item.id));
                setGuestsSelected(newGuestsSelected);
                setGuestsFilter(newGuestsSelected);
                setInTotal(newGuestsSelected.length);

                // 从选中列表中移除当前页的 ID
                const newSelectedIds = selected.filter(id => !changeIds.includes(id));
                setSelected(newSelectedIds);
            }
        },
        getCheckboxProps: record => ({
            disabled: isAddAgenda ? false : !!record.selected, // 兼容 - 添加日程时不禁止勾选， 添加嘉宾时紧张勾选
        }),
    };

    const getExpertlist = useCallback(
        async (page, pageSize, mobile, name, expertLevel) => {
            setLoading(true);
            isSearchRef.current = false;
            const obj = {
                conferenceId,
                page,
                pageSize,
                ...(expertForm.getFieldsValue() || {}),
            };

            // 当 withinConference 为 true 时，传递 searchScope=1 参数
            // searchScope 查询范围控制字段（用于expertList接口）
            // - 1：只查询本会议的嘉宾作为备选（限定范围查询）
            // - 2：查询专家库中所有专家作为备选，但会标记哪些是本会议已有的嘉宾
            obj.searchScope = withinConferenceValueRef.current ? 1 : 2;

            const res = await getAllExpertsApi(obj);
            if (res.status_code === 200) {
                setLoading(false);
                setGuestsToSelect(res.data.list);
                setTotal(res.data.totalCount);
            }
        },
        [expertForm, conferenceId],
    );

    const onChangePage = (page, pageSize) => {
        setPage(page);
        setPageSize(pageSize);
    };

    const onChangeInPage = (page, pageSize) => {
        setInPage(page);
        setInPageSize(pageSize);
    };

    const expertFormFinish = res => {
        isSearchRef.current = true;
        setFilObj({
            name: expertForm.getFieldValue('name') ? expertForm.getFieldValue('name') : '',
            mobile: expertForm.getFieldValue('mobile') ? expertForm.getFieldValue('mobile') : '',
            expertLevel: expertForm.getFieldValue('expertLevel') || expertForm.getFieldValue('expertLevel') === 0 ? expertForm.getFieldValue('expertLevel') : '',
        });
        if ((expertForm.getFieldValue('name') === undefined || expertForm.getFieldValue('name') === '') && (expertForm.getFieldValue('mobile') === undefined || expertForm.getFieldValue('mobile') === '') && (expertForm.getFieldValue('expertLevel') === undefined || expertForm.getFieldValue('expertLevel') === '')) {
            setPage(1);
        }
    };

    const filterIn = () => {
        const old = [...guestsSelected];
        if (inForm.getFieldValue('name') !== undefined || inForm.getFieldValue('name') !== '') {
            const fil = guestsFilter.filter(item => {
                return item?.memUsername.indexOf(inForm.getFieldValue('name')) > -1;
            });
            console.log(fil);
            setGuestsSelected([...fil]);
            setInTotal(fil.length);
        } else {
            setGuestsSelected([...old]);
            setInTotal(old.length);
        }
    };

    const closeExpertProfileDrawer = useCallback(() => {
        setExpertProfileDrawerOpen(false);
        setExpertProfileExpertId(null);
    }, []);

    const handleExpertProfileRefreshList = useCallback(() => {
        const { mobile, name, expertLevel } = filObj;
        getExpertlist(page, pageSize, mobile, name, expertLevel);
    }, [filObj, page, pageSize, getExpertlist]);

    const closeAddGuestsDrawer = () => {
        expertForm.resetFields();
        setVisibleAddGuestsDrawer(false);
        setExpertProfileDrawerOpen(false);
        setExpertProfileExpertId(null);
        setFilObj({
            mobile: '',
            name: '',
            expertLevel: '',
        });
        setSelected([]);
        setGuestsSelected([]);
        setGuestsFilter([]);
        setInTotal(0);
        setWithinConferenceValue(false); // 重置为默认不勾选
    };

    useEffect(() => {
        if (visibleAddGuestsDrawer) {
            setWithinConferenceValue(false); // 每次打开时重置为默认不勾选
        }
    }, [visibleAddGuestsDrawer]);

    useEffect(() => {
        const { mobile, name, expertLevel } = filObj;
        if (visibleAddGuestsDrawer) {
            if (isSearchRef.current === true) {
                getExpertlist(1, pageSize, mobile, name, expertLevel);
            } else {
                getExpertlist(page, pageSize, mobile, name, expertLevel);
            }
        }
    }, [page, pageSize, filObj, visibleAddGuestsDrawer, getExpertlist]);

    useEffect(() => {
        if (!visibleAddGuestsDrawer) {
            setGuestsSelected([]);
            setGuestsFilter([]);
            setSelected([]);
            setInTotal(0);
            setWithinConferenceValue(false); // 重置为默认不勾选
            setExpertProfileDrawerOpen(false);
            setExpertProfileExpertId(null);
        }
    }, [visibleAddGuestsDrawer]);

    const toAddExpert = () => {
        setExpertProfileExpertId(0);
        setExpertProfileDrawerOpen(true);
    };

    return (
        <div>
            <Drawer
                closable={{ placement: 'end' }}
                title="添加嘉宾"
                placement="bottom"
                size="100%"
                destroyOnHidden
                open={visibleAddGuestsDrawer}
                onClose={closeAddGuestsDrawer}
                footer={
                    <div className="custom-drawer-footer" style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={closeAddGuestsDrawer}>取消</Button>
                            <Button type="primary" onClick={addGuests}>
                                确定
                            </Button>
                        </Space>
                    </div>
                }
            >
                <div className="guest-selection-area">
                    <div className="guests-to-select box-hover-shadow">
                        <div className="guest-selection-title">
                            <span>
                                <i className="bi bi-caret-down-fill"></i> 可选嘉宾
                            </span>
                            <div className="toolbar">
                                <Button type="primary" size="small" onClick={toAddExpert}>
                                    新增专家
                                </Button>
                            </div>
                        </div>

                        <div className="guest-selection-filter form-guest-item">
                            <Form layout="inline" size="small" form={expertForm}>
                                <Form.Item label="姓名" name="name">
                                    <Input placeholder="嘉宾姓名" />
                                </Form.Item>
                                <Form.Item label="手机" name="mobile">
                                    <Input placeholder="嘉宾手机号" />
                                </Form.Item>
                                <Form.Item label="专家级别" name="expertLevel">
                                    <Select placeholder="请选择专家级别" options={expertLevels} fieldNames={{ value: 'level', label: 'label' }} style={{ width: '170px' }} />
                                </Form.Item>
                                {showWithinConference && (
                                    <Form.Item>
                                        <Checkbox
                                            checked={withinConferenceValue}
                                            onChange={e => {
                                                setWithinConferenceValue(e.target.checked);
                                            }}
                                        >
                                            仅检索会议内嘉宾
                                        </Checkbox>
                                    </Form.Item>
                                )}
                                <Form.Item>
                                    <Button icon={<i className="bi bi-search"></i>} onClick={expertFormFinish}>
                                        检索
                                    </Button>
                                </Form.Item>
                            </Form>
                        </div>

                        <Table
                            size="small"
                            columns={guestSelectionTableColumns}
                            dataSource={guestsToSelect}
                            rowKey={row => row.id}
                            rowSelection={{
                                type: 'checkbox',
                                ...guestToSelectSelection,
                            }}
                            loading={loading}
                            pagination={{
                                total: total,
                                showTotal: () => `共${total}条`,
                                current: page,
                                pageSize: pageSize,
                                pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                                onChange: onChangePage,
                                showQuickJumper: true,
                                showSizeChanger: true,
                            }}
                            onRow={record => ({
                                onClick: () => {
                                    const isDisabled = isAddAgenda ? false : !!record.selected;
                                    if (isDisabled) return;

                                    const isSelected = selected.includes(record.id);
                                    guestToSelectSelection.onSelect(record, !isSelected);
                                },
                                style: { cursor: 'pointer' },
                            })}
                        />
                    </div>

                    {/* 移除中间的按钮操作区域 */}

                    <div className="guests-selected box-hover-shadow">
                        <div className="guest-selection-title">
                            <i className="bi bi-caret-down-fill"></i> 已选嘉宾
                        </div>
                        <div className="guest-selection-filter">
                            <Form layout="inline" size="small" form={inForm}>
                                <Form.Item label="姓名" name="name">
                                    <Input placeholder="嘉宾姓名" />
                                </Form.Item>
                                <Form.Item>
                                    <Button onClick={filterIn} icon={<i className="bi bi-funnel"></i>}>
                                        过滤
                                    </Button>
                                </Form.Item>
                            </Form>
                        </div>

                        <Table
                            size="small"
                            columns={guestSelectedTableColumns}
                            dataSource={guestsSelected}
                            rowKey={rows => rows.id}
                            pagination={{
                                // total: inTotal,
                                showTotal: () => `共${inTotal}条`,
                                current: inPage,
                                pageSize: inPageSize,
                                pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                                onChange: onChangeInPage,
                                showQuickJumper: true,
                                showSizeChanger: true,
                            }}
                        />
                    </div>
                </div>
            </Drawer>

            <ExpertProfileDrawer
                open={expertProfileDrawerOpen}
                expertId={expertProfileExpertId}
                openInEditMode={false}
                onClose={closeExpertProfileDrawer}
                onRefreshList={handleExpertProfileRefreshList}
                onOpenExpertDetail={id => {
                    if (!id) {
                        return;
                    }
                    setExpertProfileExpertId(id);
                }}
                zIndex={1100}
            />
        </div>
    );
};

export default AddGuest;
